/**
 * Modal for viewing friends and blocked users
 */
import { Avatar } from './Avatar';
import './FriendsModal.css';

interface FriendsModalProps {
  friends: Set<string>;
  blocked: Set<string>;
  onClose: () => void;
  onRemoveFriend: (pubkey: string) => void;
  onUnblock: (pubkey: string) => void;
  onViewProfile: (pubkey: string) => void;
  getDisplayName: (pubkey: string) => string;
  getAvatarUrl?: (pubkey: string) => string | undefined;
  /** Check if a friend is online */
  isOnline?: (pubkey: string) => boolean;
  /** Count of online friends */
  onlineFriendsCount?: number;
}

export function FriendsModal({
  friends,
  blocked,
  onClose,
  onRemoveFriend,
  onUnblock,
  onViewProfile,
  getDisplayName,
  getAvatarUrl,
  isOnline,
  onlineFriendsCount = 0,
}: FriendsModalProps) {
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Sort friends: online first, then alphabetically by display name
  const friendsList = Array.from(friends).sort((a, b) => {
    const aOnline = isOnline?.(a) ? 1 : 0;
    const bOnline = isOnline?.(b) ? 1 : 0;
    if (aOnline !== bOnline) {
      return bOnline - aOnline; // Online first
    }
    return getDisplayName(a).localeCompare(getDisplayName(b));
  });
  const blockedList = Array.from(blocked);

  return (
    <div className="friends-modal-overlay" onClick={handleOverlayClick}>
      <div className="friends-modal">
        <button className="modal-close-btn" onClick={onClose}>×</button>
        <h2 className="friends-modal-title">Social</h2>

        {/* Friends Section */}
        <div className="friends-section">
          <h3 className="section-title">
            Friends ({friendsList.length})
            {onlineFriendsCount > 0 && (
              <span className="online-count">{onlineFriendsCount} online</span>
            )}
          </h3>
          {friendsList.length === 0 ? (
            <p className="empty-message">No friends yet. Click on a user's profile to add them as a friend.</p>
          ) : (
            <ul className="user-list">
              {friendsList.map(pubkey => (
                <li key={pubkey} className={`user-item ${isOnline?.(pubkey) ? 'is-online' : ''}`}>
                  <button
                    className="user-info"
                    onClick={() => {
                      onViewProfile(pubkey);
                      onClose();
                    }}
                  >
                    <div className="avatar-with-status">
                      <Avatar
                        src={getAvatarUrl?.(pubkey)}
                        displayName={getDisplayName(pubkey)}
                        size="small"
                      />
                      {isOnline?.(pubkey) && <span className="online-indicator" title="Online" />}
                    </div>
                    <span className="user-name">{getDisplayName(pubkey)}</span>
                  </button>
                  <button
                    className="action-btn remove-btn"
                    onClick={() => onRemoveFriend(pubkey)}
                    title="Remove friend"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Blocked Section */}
        <div className="blocked-section">
          <h3 className="section-title">
            Blocked ({blockedList.length})
          </h3>
          {blockedList.length === 0 ? (
            <p className="empty-message">No blocked users.</p>
          ) : (
            <ul className="user-list">
              {blockedList.map(pubkey => (
                <li key={pubkey} className="user-item">
                  <div className="user-info">
                    <Avatar
                      src={getAvatarUrl?.(pubkey)}
                      displayName={getDisplayName(pubkey)}
                      size="small"
                    />
                    <span className="user-name blocked">{getDisplayName(pubkey)}</span>
                  </div>
                  <button
                    className="action-btn unblock-btn"
                    onClick={() => onUnblock(pubkey)}
                    title="Unblock user"
                  >
                    Unblock
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
